import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Shield, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { isFirebaseConfigured, signInWithGoogleAndGetIdToken, signOutFirebase } from "@/lib/firebase";
import { useAuth } from "@/context/AuthContext";

/**
 * /admin/login — Google Sign-In gate for the admin panel.
 *
 * Flow:
 *   1. User clicks "Continue with Google" → Firebase popup
 *   2. We get a Firebase ID token, POST to /api/admin/google-login
 *   3. Backend verifies the token + whitelist, returns ShiftQuote JWT
 *   4. We mirror the JWT to BOTH localStorage.admin_token (per spec) AND
 *      localStorage.auth_token (so the existing axios interceptor + the
 *      AdminDashboard's /admin/* API calls keep working without rewrites)
 *   5. Redirect to /admin
 *
 * (The hidden ?emergency=true "break-glass" form and its backend endpoint
 * were removed in security-fix-oct26.)
 */
export default function AdminLogin() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const configured = isFirebaseConfigured();

  // If an admin_token is already present, skip straight to the dashboard.
  useEffect(() => {
    if (localStorage.getItem("admin_token")) {
      navigate("/admin", { replace: true });
    }
  }, [navigate]);

  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      const { idToken } = await signInWithGoogleAndGetIdToken();
      const res = await api.post("/admin/google-login", { id_token: idToken });
      const { token, user } = res.data;
      if (!token || !user || user.role !== "admin") {
        throw new Error("Server returned a non-admin response");
      }
      localStorage.setItem("admin_token", token);
      localStorage.setItem("auth_token", token);
      try { await checkAuth?.(); } catch { /* non-fatal */ }
      navigate("/admin", { replace: true });
    } catch (e) {
      await signOutFirebase();
      const msg =
        e?.response?.data?.detail ||
        (e?.code === "auth/popup-closed-by-user" ? "Sign-in cancelled" : null) ||
        e?.message ||
        "Sign-in failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Normal Magic Link / Google view ─────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-200 flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="block text-xs uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] mb-8 text-center"
          data-testid="admin-login-back"
        >
          &larr; SHIFTQUOTE
        </Link>

        <div className="border border-white/10 bg-[#121214] p-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-full bg-[#FF3B30]/15 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#FF3B30]" />
            </div>
            <div>
              <div className="label-eyebrow text-[#FF3B30]">ADMIN</div>
              <h1 className="font-heading text-2xl font-black tracking-tight">Sign in</h1>
            </div>
          </div>
          <p className="text-sm text-zinc-500 mb-8 mt-3">
            Restricted access. Only authorized Google accounts can enter.
          </p>

          {!configured && (
            <div
              className="mb-6 flex items-start gap-2 p-3 border border-amber-500/30 bg-amber-500/5 text-amber-200 text-xs"
              data-testid="admin-login-config-warning"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                Firebase Web SDK is not configured. Set
                {" "}<code className="bg-black/40 px-1">REACT_APP_FIREBASE_API_KEY</code>,{" "}
                <code className="bg-black/40 px-1">REACT_APP_FIREBASE_PROJECT_ID</code>, and{" "}
                <code className="bg-black/40 px-1">REACT_APP_FIREBASE_APP_ID</code> in <code>frontend/.env</code>, then restart.
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={handleGoogle}
            disabled={loading || !configured}
            data-testid="admin-google-signin"
            className="w-full flex items-center justify-center gap-3 px-5 py-3.5 bg-white text-zinc-900 font-medium tracking-tight disabled:opacity-50 disabled:cursor-not-allowed hover:bg-zinc-100 transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Signing in…</span>
              </>
            ) : (
              <>
                <GoogleIcon />
                <span>Continue with Google</span>
              </>
            )}
          </button>

          <div className="flex items-center gap-3 my-5 text-xs uppercase tracking-widest text-zinc-600">
            <div className="flex-1 h-px bg-white/10" />
            <span>or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <Link
            to="/login"
            data-testid="admin-login-email-link"
            className="w-full flex items-center justify-center gap-2 px-5 py-3.5 border border-white/15 bg-transparent text-zinc-200 font-medium tracking-tight hover:bg-white/[0.04] transition-colors"
          >
            Sign in with email &amp; password
          </Link>

          {error && (
            <div
              className="mt-5 p-3 border border-red-500/30 bg-red-500/5 text-red-300 text-sm"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-white/5 text-xs text-zinc-600 leading-relaxed">
            This screen is for ShiftQuote staff only. Owner and shop accounts can also sign in from{" "}
            <Link to="/login" className="text-[#FF3B30] underline" data-testid="admin-login-user-link">
              the standard sign-in page
            </Link>
            .
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
    </svg>
  );
}
